import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { PaymentButton } from '@/components/PaymentButton';
import { FeesPaymentsSection } from '@/components/FeesPaymentsSection';
import {
  MapPin, Users, Calendar, Clock, CheckCircle, AlertCircle, MessageSquare, Award, Send, Download,
  Mail, FileText, ExternalLink, IndianRupee, TrendingUp, UserPlus, PieChart, BarChart3, CreditCard,
  GraduationCap, XCircle, Filter, Building2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, LineChart, Line
} from 'recharts';
import { 
  fetchFeeStructure as fetchFeesFromService, 
  getExamFee as feeServiceGetExamFee, 
  getExamCenterFee,
  getExaminationTypes,
  getDefaultFees,
  DISCOUNT_RATES 
} from '@/utils/feeService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444'];

// Get default examinations for immediate display (will be updated from backend)
const getInitialExaminations = () => getExaminationTypes(getDefaultFees());

// Get fee for examination using fee service (no discount for exam center)
const getExamFee = (code, feeStructure = null) => feeServiceGetExamFee(code, feeStructure);

// Exam Center Registration Fee
const REGISTRATION_FEE = 150000;

function StatCard({ icon: Icon, label, value, color, subtext, trend }) {
  return (
    <Card className="imcac-card">
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {subtext && <p className="text-xs text-slate-400">{subtext}</p>}
        </div>
        {trend && (
          <div className={`text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className="h-3 w-3 inline mr-1" />
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ExaminationCenterDashboard() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [studentResults, setStudentResults] = useState([]);
  const [allocatedCandidates, setAllocatedCandidates] = useState([]);
  const [examinations, setExaminations] = useState(getInitialExaminations());
  const [feeStructure, setFeeStructure] = useState(null);
  
  // Forms
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageForm, setMessageForm] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [showNominationForm, setShowNominationForm] = useState(false);
  const [nominations, setNominations] = useState([]);
  const [nominationForm, setNominationForm] = useState({
    full_name: '', email: '', phone: '', date_of_birth: '', gender: '',
    nationality: 'India', qualification: '', medical_reg_number: '',
    examination: '', route: '1', specialty: '', years_of_practice: ''
  });
  const [submittingNomination, setSubmittingNomination] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedNomination, setSelectedNomination] = useState(null);
  
  // Legacy enrollment form (keeping for backward compatibility)
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [enrollForm, setEnrollForm] = useState({ student_email: '', examination_code: '', route: 1, notes: '' });
  
  // Filters
  const [resultFilter, setResultFilter] = useState('');
  const [examFilter, setExamFilter] = useState('');
  const [financialPeriod, setFinancialPeriod] = useState('all');
  
  // Payment & Bank Transfer
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [showBankTransferModal, setShowBankTransferModal] = useState(null);
  const [showRegFeeTransferModal, setShowRegFeeTransferModal] = useState(false);
  const [bankTransferForm, setBankTransferForm] = useState({
    transfer_reference: '',
    transfer_date: '',
    bank_name: '',
    amount: 0,
    remarks: ''
  });

  // Fetch fee structure from backend
  const loadFeeStructure = async () => {
    try {
      const fees = await fetchFeesFromService();
      setFeeStructure(fees);
      setExaminations(getExaminationTypes(fees));
    } catch (err) { 
      console.error('Failed to fetch fee structure', err);
      setFeeStructure(getDefaultFees());
    }
  };

  useEffect(() => {
    if (token) {
      fetchAll();
      loadFeeStructure();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Fetch messages
      const msgsRes = await axios.get(`${API}/portal/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      setMessages(msgsRes.data);

      // Fetch certificates
      const certsRes = await axios.get(`${API}/my-certificates/examination-center`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      setCertificates(certsRes.data);

      // Try to fetch enhanced dashboard (for approved centers)
      try {
        const dashRes = await axios.get(`${API}/examination-center/enhanced-dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboardData(dashRes.data);
        setError(null);
        
        // Fetch additional data for approved centers
        fetchEnrolledStudents();
        fetchFinancialSummary();
        fetchStatistics();
        fetchStudentResults();
        fetchAllocatedCandidates();
        fetchPaymentHistory();
        fetchBankDetails();
        fetchNominations();
      } catch (err) {
        if (err.response?.status === 404) {
          // No approved center, fetch applications
          const appsRes = await axios.get(`${API}/my-examination-center-applications`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] }));
          setApplications(appsRes.data);
        } else {
          setError(err.response?.data?.detail || 'Failed to load dashboard');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrolledStudents = async () => {
    try {
      const res = await axios.get(`${API}/examination-center/enrolled-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrolledStudents(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchNominations = async () => {
    try {
      const res = await axios.get(`${API}/examination-center/nominations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNominations(res.data);
    } catch (err) { console.error(err); }
  };

  const handleNominateStudent = async (e) => {
    e.preventDefault();
    if (!nominationForm.full_name || !nominationForm.email || !nominationForm.examination || !nominationForm.date_of_birth) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmittingNomination(true);
    try {
      await axios.post(`${API}/examination-center/nominate-student`, {
        ...nominationForm,
        years_of_practice: parseInt(nominationForm.years_of_practice) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student nominated successfully! Proceed to payment.');
      setShowNominationForm(false);
      setNominationForm({
        full_name: '', email: '', phone: '', date_of_birth: '', gender: '',
        nationality: 'India', qualification: '', medical_reg_number: '',
        examination: '', route: '1', specialty: '', years_of_practice: ''
      });
      fetchNominations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to nominate student');
    } finally { setSubmittingNomination(false); }
  };

  const handlePayNominationFee = (nomination) => {
    setSelectedNomination(nomination);
    setShowPaymentModal(true);
  };

  const handleNominationPaymentSuccess = async (paymentId, nomination) => {
    try {
      await axios.post(`${API}/examination-center/nominations/${nomination.id}/payment`, {
        payment_id: paymentId,
        amount: nomination.base_fee
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment successful! Student account created and credentials sent.');
      setShowPaymentModal(false);
      setSelectedNomination(null);
      fetchNominations();
      fetchPaymentHistory();
    } catch (err) {
      toast.error('Payment recorded but failed to process. Please contact support.');
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const res = await axios.get(`${API}/examination-center/financial-summary?period=${financialPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFinancialSummary(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchStatistics = async () => {
    try {
      const res = await axios.get(`${API}/examination-center/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchAllocatedCandidates = async () => {
    try {
      const res = await axios.get(`${API}/examination-center/allocated-candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllocatedCandidates(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchStudentResults = async () => {
    try {
      let url = `${API}/examination-center/student-results`;
      const params = new URLSearchParams();
      if (resultFilter) params.append('status', resultFilter);
      if (examFilter) params.append('examination_code', examFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudentResults(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchPaymentHistory = async () => {
    try {
      const res = await axios.get(`${API}/examination-center/payment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchBankDetails = async () => {
    try {
      const res = await axios.get(`${API}/examination-center/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankDetails(res.data);
    } catch (err) { console.error(err); }
  };

  const submitBankTransfer = async () => {
    if (!bankTransferForm.transfer_reference || !bankTransferForm.transfer_date || !bankTransferForm.bank_name) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API}/examination-center/enrollments/${showBankTransferModal.id}/bank-transfer`, {
        ...bankTransferForm,
        amount: showBankTransferModal.discounted_fee
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bank transfer details submitted. Awaiting admin verification.');
      setShowBankTransferModal(null);
      setBankTransferForm({ transfer_reference: '', transfer_date: '', bank_name: '', amount: 0, remarks: '' });
      fetchEnrolledStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bank transfer');
    }
  };

  const submitRegFeeBankTransfer = async () => {
    if (!bankTransferForm.transfer_reference || !bankTransferForm.transfer_date || !bankTransferForm.bank_name) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API}/examination-center/registration-fee-bank-transfer`, {
        ...bankTransferForm,
        amount: REGISTRATION_FEE
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Registration fee bank transfer submitted. Awaiting admin verification.');
      setShowRegFeeTransferModal(false);
      setBankTransferForm({ transfer_reference: '', transfer_date: '', bank_name: '', amount: 0, remarks: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bank transfer');
    }
  };

  const downloadInvoice = async (enrollmentId, invoiceType = 'auto') => {
    try {
      const res = await axios.get(`${API}/examination-center/generate-invoice/${enrollmentId}?invoice_type=${invoiceType}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${enrollmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (err) {
      toast.error('Failed to download invoice');
    }
  };

  useEffect(() => {
    if (dashboardData) {
      fetchFinancialSummary();
    }
  }, [financialPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dashboardData) {
      fetchStudentResults();
    }
  }, [resultFilter, examFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnrollStudent = async () => {
    if (!enrollForm.student_email || !enrollForm.examination_code) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      const res = await axios.post(`${API}/examination-center/enroll-student`, enrollForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Student enrolled! Discounted fee: ₹${res.data.discounted_fee.toLocaleString()}`);
      setShowEnrollForm(false);
      setEnrollForm({ student_email: '', examination_code: '', route: 1, notes: '' });
      fetchEnrolledStudents();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enroll student');
    }
  };

  const updatePaymentStatus = async (enrollmentId, status) => {
    try {
      await axios.put(`${API}/examination-center/enrollments/${enrollmentId}/payment?payment_status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment status updated');
      fetchEnrolledStudents();
      fetchFinancialSummary();
    } catch (err) {
      toast.error('Failed to update payment status');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.subject || !messageForm.message) {
      toast.error('Please fill all fields');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/portal/messages`, {
        ...messageForm,
        portal_type: 'examination_center'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Message sent to IMCAC');
      setShowMessageForm(false);
      setMessageForm({ subject: '', message: '' });
      fetchAll();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: { bg: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Under Review' },
      in_review: { bg: 'bg-amber-100 text-amber-700', icon: Clock, label: 'In Review' },
      approved: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Approved' },
      rejected: { bg: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'Rejected' },
    };
    const style = styles[status] || styles.submitted;
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} rounded-sm px-3 py-1`}>
        <Icon className="h-3 w-3 mr-1" /> {style.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  const unreadMessages = messages.filter(m => m.direction === 'outgoing' && m.status === 'unread').length;

  // If no approved center, show application status
  if (!dashboardData) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen" data-testid="exam-center-dashboard-pending">
        <section className="bg-[#1B2A4A] py-10">
          <div className="imcac-container">
            <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Examination Center Portal</p>
            <h1 className="text-3xl font-bold text-white tracking-tight font-heading">Welcome, {user?.full_name}</h1>
            <p className="text-slate-300 text-sm mt-1">Manage your examination center accreditation</p>
          </div>
        </section>

        <div className="imcac-container py-8">
          <Card className="imcac-card">
            <Tabs defaultValue="applications" className="w-full">
              <TabsList className="w-full justify-start h-auto flex-wrap bg-[#F8FAFC] rounded-none border-b border-slate-200 p-1 gap-1">
                <TabsTrigger value="applications" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                  <FileText className="h-4 w-4 mr-1" /> Applications
                </TabsTrigger>
                <TabsTrigger value="messages" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                  <MessageSquare className="h-4 w-4 mr-1" /> Messages
                </TabsTrigger>
              </TabsList>

              <TabsContent value="applications" className="p-5">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-[#1B2A4A] font-heading">Your Applications</h2>
                  <Button asChild className="bg-[#1B2A4A] text-white rounded-sm">
                    <a href="/apply/examination-center">New Application</a>
                  </Button>
                </div>

                {applications.length === 0 ? (
                  <div className="text-center py-12">
                    <MapPin className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">No Applications Yet</h3>
                    <p className="text-sm text-slate-500 mb-4">Apply to become an IMCAC accredited examination center</p>
                    <Button asChild className="bg-[#C5A572] text-white rounded-sm">
                      <a href="/apply/examination-center">Apply Now</a>
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {applications.map((app) => (
                      <div key={app.id} className="border border-slate-200 rounded-sm p-4 hover:border-[#C5A572] transition-colors">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-[#1B2A4A]">{app.center_name}</h3>
                            <p className="text-sm text-slate-500">{app.city}, {app.country}</p>
                            <p className="text-xs text-slate-400 mt-1">Submitted: {new Date(app.created_at).toLocaleDateString()}</p>
                            {app.center_id && <p className="text-xs text-[#C5A572] font-mono mt-1">Center ID: {app.center_id}</p>}
                          </div>
                          {getStatusBadge(app.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="messages" className="p-5">
                {/* Messages content - same as before */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#1B2A4A]">Messages</h3>
                  <Button onClick={() => setShowMessageForm(!showMessageForm)} className="bg-[#C5A572] text-white rounded-sm text-sm">
                    <Send className="h-4 w-4 mr-1" /> New Message
                  </Button>
                </div>
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No messages yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div key={msg.id} className="border rounded-sm p-4">
                        <h4 className="text-sm font-semibold text-[#1B2A4A]">{msg.subject}</h4>
                        <p className="text-sm text-slate-600 mt-1">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>
    );
  }

  // Approved center dashboard
  const { center, stats, financials } = dashboardData;

  // Prepare chart data
  const resultsChartData = statistics ? [
    { name: 'Pass', value: statistics.results.pass, color: '#22c55e' },
    { name: 'Merit', value: statistics.classifications?.merit || 0, color: '#3b82f6' },
    { name: 'Distinction', value: statistics.classifications?.distinction || 0, color: '#f59e0b' },
    { name: 'Fail', value: statistics.results.fail, color: '#ef4444' }
  ].filter(d => d.value > 0) : [];

  const examBreakdownData = statistics?.examination_breakdown?.map(e => ({
    name: e.examination,
    passed: e.passed,
    failed: e.failed,
    total: e.total
  })) || [];

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="exam-center-dashboard">
      {/* Header */}
      <section className="bg-[#1B2A4A] py-10">
        <div className="imcac-container">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Examination Center Portal</p>
              <h1 className="text-3xl font-bold text-white tracking-tight font-heading">{center.name}</h1>
              <p className="text-slate-300 text-sm mt-1">
                <span className="font-mono text-[#C5A572]">{center.id}</span> · {center.city}, {center.country}
              </p>
            </div>
            <div className="flex gap-2">
              <Badge className="bg-green-500/20 text-green-100 text-xs px-3 py-1">
                <CheckCircle className="h-3 w-3 mr-1" /> Accredited
              </Badge>
              <Badge className="bg-white/10 text-white text-xs px-3 py-1">
                30% Revenue Share
              </Badge>
            </div>
          </div>
        </div>
      </section>

      <div className="imcac-container py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="center-stats">
          <StatCard icon={Users} label="Total Enrolled" value={stats.total_enrollments} color="bg-blue-100 text-blue-600" />
          <StatCard icon={CreditCard} label="Paid" value={stats.paid_enrollments} color="bg-green-100 text-green-600" />
          <StatCard icon={Clock} label="Pending Payment" value={stats.pending_payments} color="bg-amber-100 text-amber-600" />
          <StatCard icon={Calendar} label="Upcoming Exams" value={stats.upcoming_exams} color="bg-purple-100 text-purple-600" />
          <StatCard 
            icon={CheckCircle} 
            label="Pass Rate" 
            value={`${stats.pass_rate}%`} 
            color="bg-emerald-100 text-emerald-600" 
          />
          <StatCard 
            icon={IndianRupee} 
            label="Your Revenue" 
            value={`₹${(financials.center_revenue || 0).toLocaleString()}`} 
            color="bg-[#C5A572]/20 text-[#C5A572]"
            subtext="30% share"
          />
        </div>

        {/* Main Tabs */}
        <Card className="imcac-card">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start h-auto flex-wrap bg-[#F8FAFC] rounded-none border-b border-slate-200 p-1 gap-1">
              <TabsTrigger value="overview" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <PieChart className="h-4 w-4 mr-1" /> Overview
              </TabsTrigger>
              <TabsTrigger value="enrollments" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-enrollments">
                <UserPlus className="h-4 w-4 mr-1" /> Student Enrollment
              </TabsTrigger>
              <TabsTrigger value="fees" className="rounded-sm text-sm data-[state=active]:bg-[#22c55e] data-[state=active]:text-white" data-testid="tab-fees">
                <CreditCard className="h-4 w-4 mr-1" /> Fees & Payments
              </TabsTrigger>
              <TabsTrigger value="financials" className="rounded-sm text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-white" data-testid="tab-financials">
                <IndianRupee className="h-4 w-4 mr-1" /> Financials
              </TabsTrigger>
              <TabsTrigger value="results" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-results">
                <BarChart3 className="h-4 w-4 mr-1" /> Results & Statistics
              </TabsTrigger>
              <TabsTrigger value="exams" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <Calendar className="h-4 w-4 mr-1" /> Scheduled Exams
              </TabsTrigger>
              <TabsTrigger value="messages" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <MessageSquare className="h-4 w-4 mr-1" /> Messages {unreadMessages > 0 && <Badge className="ml-1 bg-red-500 text-white text-xs">{unreadMessages}</Badge>}
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="p-5">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Results Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Results Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {resultsChartData.length > 0 ? (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPie>
                            <Pie
                              data={resultsChartData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={80}
                              paddingAngle={5}
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {resultsChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </RechartsPie>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-center py-8 text-slate-500">No results data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Examination Performance */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Examination Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {examBreakdownData.length > 0 ? (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={examBreakdownData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="passed" fill="#22c55e" name="Passed" />
                            <Bar dataKey="failed" fill="#ef4444" name="Failed" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-center py-8 text-slate-500">No examination data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Enrollments */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Recent Enrollments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboardData.recent_enrollments?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-[#F8FAFC]">
                            <TableHead className="text-xs font-bold">Student</TableHead>
                            <TableHead className="text-xs font-bold">Examination</TableHead>
                            <TableHead className="text-xs font-bold">Fee</TableHead>
                            <TableHead className="text-xs font-bold">Payment</TableHead>
                            <TableHead className="text-xs font-bold">Enrolled</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dashboardData.recent_enrollments.slice(0, 5).map((e) => (
                            <TableRow key={e.id}>
                              <TableCell className="text-sm">{e.student_email}</TableCell>
                              <TableCell><Badge className="bg-slate-100">{e.examination_code}</Badge></TableCell>
                              <TableCell className="text-sm">₹{e.discounted_fee?.toLocaleString()}</TableCell>
                              <TableCell>
                                <Badge className={e.payment_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                  {e.payment_status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">{new Date(e.enrolled_at).toLocaleDateString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-4 text-slate-500">No enrollments yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Student Nomination Tab (same as Accreditation Portal) */}
            <TabsContent value="enrollments" className="p-5" data-testid="enrollments-tab-content">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1B2A4A]">Student Nominations</h3>
                  <p className="text-sm text-slate-500">Nominate students for IMCAC examinations</p>
                </div>
                <Button onClick={() => setShowNominationForm(true)} className="bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-sm" data-testid="nominate-student-btn">
                  <UserPlus className="h-4 w-4 mr-1" /> Nominate Student
                </Button>
              </div>

              {/* Nomination Form Modal */}
              {showNominationForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                    <CardHeader className="bg-[#1B2A4A] text-white rounded-t-lg">
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" /> Nominate Student for Examination
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <form onSubmit={handleNominateStudent} className="space-y-4">
                        <div className="bg-[#C5A572]/10 p-3 rounded-sm border border-[#C5A572]/30 mb-4">
                          <p className="text-sm text-[#1B2A4A]"><strong>Nominating from:</strong> {dashboardData?.center_name || 'Your Center'}</p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs">Full Name *</Label>
                            <Input value={nominationForm.full_name} onChange={e => setNominationForm({...nominationForm, full_name: e.target.value})} className="mt-1 rounded-sm" required />
                          </div>
                          <div>
                            <Label className="text-xs">Email *</Label>
                            <Input type="email" value={nominationForm.email} onChange={e => setNominationForm({...nominationForm, email: e.target.value})} className="mt-1 rounded-sm" required />
                          </div>
                          <div>
                            <Label className="text-xs">Phone</Label>
                            <Input value={nominationForm.phone} onChange={e => setNominationForm({...nominationForm, phone: e.target.value})} className="mt-1 rounded-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">Date of Birth *</Label>
                            <Input type="date" value={nominationForm.date_of_birth} onChange={e => setNominationForm({...nominationForm, date_of_birth: e.target.value})} className="mt-1 rounded-sm" required />
                          </div>
                          <div>
                            <Label className="text-xs">Gender</Label>
                            <Select value={nominationForm.gender} onValueChange={v => setNominationForm({...nominationForm, gender: v})}>
                              <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select gender" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Nationality</Label>
                            <Input value={nominationForm.nationality} onChange={e => setNominationForm({...nominationForm, nationality: e.target.value})} className="mt-1 rounded-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">Qualification</Label>
                            <Input value={nominationForm.qualification} onChange={e => setNominationForm({...nominationForm, qualification: e.target.value})} className="mt-1 rounded-sm" placeholder="MBBS, BDS, etc." />
                          </div>
                          <div>
                            <Label className="text-xs">Medical Reg. Number</Label>
                            <Input value={nominationForm.medical_reg_number} onChange={e => setNominationForm({...nominationForm, medical_reg_number: e.target.value})} className="mt-1 rounded-sm" />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs">Examination *</Label>
                            <Select value={nominationForm.examination} onValueChange={v => setNominationForm({...nominationForm, examination: v})}>
                              <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select examination" /></SelectTrigger>
                              <SelectContent>
                                {examinations.map(exam => (
                                  <SelectItem key={exam.value} value={exam.value}>
                                    {exam.label} - ₹{exam.fee.toLocaleString()}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Route</Label>
                            <Select value={nominationForm.route} onValueChange={v => setNominationForm({...nominationForm, route: v})}>
                              <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">Route 1 - Standard</SelectItem>
                                <SelectItem value="2">Route 2 - Experience</SelectItem>
                                <SelectItem value="3">Route 3 - International</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Years of Practice</Label>
                            <Input type="number" value={nominationForm.years_of_practice} onChange={e => setNominationForm({...nominationForm, years_of_practice: e.target.value})} className="mt-1 rounded-sm" min="0" />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs">Specialty (if applicable)</Label>
                            <Input value={nominationForm.specialty} onChange={e => setNominationForm({...nominationForm, specialty: e.target.value})} className="mt-1 rounded-sm" placeholder="e.g., Dermatology, Plastic Surgery" />
                          </div>
                        </div>
                        
                        <div className="flex gap-2 justify-end pt-4 border-t">
                          <Button type="button" variant="outline" onClick={() => setShowNominationForm(false)} className="rounded-sm">Cancel</Button>
                          <Button type="submit" disabled={submittingNomination} className="bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-sm">
                            {submittingNomination ? 'Submitting...' : 'Submit Nomination'}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Nominations List */}
              {nominations.length === 0 ? (
                <Card className="bg-slate-50">
                  <CardContent className="p-8 text-center">
                    <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No students nominated yet</p>
                    <p className="text-sm text-slate-400 mt-1">Click "Nominate Student" to submit your first nomination</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <Card className="bg-blue-50">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-blue-700">{nominations.length}</p>
                        <p className="text-xs text-blue-600">Total Nominated</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-amber-50">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-amber-700">{nominations.filter(n => n.payment_status === 'pending').length}</p>
                        <p className="text-xs text-amber-600">Pending Payment</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-green-700">{nominations.filter(n => n.payment_status === 'paid').length}</p>
                        <p className="text-xs text-green-600">Paid</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#1B2A4A]">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-white">₹{nominations.filter(n => n.payment_status === 'paid').reduce((sum, n) => sum + (n.discounted_fee || 0), 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-300">Total Collected</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* All Nominations Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">All Nominations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-[#F8FAFC]">
                            <TableHead className="text-xs font-bold">Student</TableHead>
                            <TableHead className="text-xs font-bold">Examination</TableHead>
                            <TableHead className="text-xs font-bold">Fee</TableHead>
                            <TableHead className="text-xs font-bold">Payment</TableHead>
                            <TableHead className="text-xs font-bold">Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nominations.map(nom => (
                            <TableRow key={nom.id}>
                              <TableCell>
                                <div>
                                  <p className="text-sm font-medium">{nom.full_name}</p>
                                  <p className="text-xs text-slate-500">{nom.email}</p>
                                </div>
                              </TableCell>
                              <TableCell><Badge className="bg-slate-100 text-slate-700">{nom.examination}</Badge></TableCell>
                              <TableCell className="text-sm font-medium">₹{nom.base_fee?.toLocaleString()}</TableCell>
                              <TableCell>
                                {nom.payment_status === 'paid' ? (
                                  <Badge className="bg-green-100 text-green-700">Paid</Badge>
                                ) : nom.payment_status === 'pending_verification' ? (
                                  <Badge className="bg-blue-100 text-blue-700">Pending Verification</Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">{nom.created_at ? new Date(nom.created_at).toLocaleDateString() : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Fees & Payments Tab */}
            <TabsContent value="fees" className="p-5" data-testid="fees-tab-content">
              <div className="space-y-6">
                {/* Registration Fee - Show if approved but not paid */}
                {dashboardData?.status === 'approved' && !dashboardData?.registration_fee_paid && (
                  <Card className="border-l-4 border-l-amber-500">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                        <AlertCircle className="h-4 w-4" /> Registration Fee Due
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between p-4 bg-amber-50 rounded-sm">
                        <div>
                          <p className="font-semibold text-[#1B2A4A]">Examination Center Registration Fee</p>
                          <p className="text-sm text-slate-500">One-time registration fee for accredited examination centers</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-amber-600">₹{REGISTRATION_FEE.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <PaymentButton
                          paymentType="exam_center_registration"
                          amount={REGISTRATION_FEE}
                          referenceId={dashboardData?.center?.id}
                          description="Examination Center Registration Fee"
                          onSuccess={() => {
                            toast.success('Registration fee paid successfully!');
                            fetchAll();
                          }}
                          onError={(error) => toast.error(error || 'Payment failed')}
                          className="flex-1 bg-[#C5A572] hover:bg-[#B8956A] text-white rounded-sm"
                        >
                          <CreditCard className="h-4 w-4 mr-2" /> Pay Registration Fee
                        </PaymentButton>
                        <Button 
                          variant="outline" 
                          className="flex-1 rounded-sm border-[#1B2A4A] text-[#1B2A4A]"
                          onClick={() => setShowRegFeeTransferModal(true)}
                        >
                          <Building2 className="h-4 w-4 mr-2" /> Bank Transfer
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Use Shared FeesPaymentsSection Component */}
                <FeesPaymentsSection
                  token={token}
                  pendingPayments={nominations.filter(n => n.payment_status === 'pending')}
                  pendingVerification={nominations.filter(n => n.payment_status === 'pending_verification')}
                  onPaymentSuccess={(paymentId, item) => handleNominationPaymentSuccess(paymentId, item)}
                  onBankTransferSubmit={async (nominationId, transferDetails) => {
                    await axios.post(`${API}/examination-center/submit-bank-transfer/${nominationId}`, transferDetails, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    fetchNominations();
                  }}
                  portalType="examination_center"
                  discountRate={0}
                  getExamFee={getExamFee}
                  feeStructure={feeStructure}
                />
              </div>
            </TabsContent>

            {/* Bank Transfer Modal */}
            {showBankTransferModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowBankTransferModal(null)}>
                <Card className="max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Submit Bank Transfer Details</h3>
                    <div className="space-y-4">
                      <div className="p-3 bg-slate-50 rounded-sm">
                        <p className="text-sm font-medium">{showBankTransferModal.student_name || showBankTransferModal.student_email}</p>
                        <p className="text-xs text-slate-500">{showBankTransferModal.examination_code} - ₹{showBankTransferModal.discounted_fee?.toLocaleString()}</p>
                      </div>
                      
                      {bankDetails && (
                        <div className="p-3 bg-blue-50 rounded-sm text-xs">
                          <p className="font-semibold text-blue-700 mb-1">Transfer to:</p>
                          <p>{bankDetails.account_name}</p>
                          <p>A/C: {bankDetails.account_number} | IFSC: {bankDetails.ifsc_code}</p>
                        </div>
                      )}
                      
                      <div>
                        <Label className="text-sm">Transfer Reference / UTR *</Label>
                        <Input
                          value={bankTransferForm.transfer_reference}
                          onChange={e => setBankTransferForm({...bankTransferForm, transfer_reference: e.target.value})}
                          placeholder="Enter UTR or transaction reference"
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Transfer Date *</Label>
                        <Input
                          type="date"
                          value={bankTransferForm.transfer_date}
                          onChange={e => setBankTransferForm({...bankTransferForm, transfer_date: e.target.value})}
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Bank Name *</Label>
                        <Input
                          value={bankTransferForm.bank_name}
                          onChange={e => setBankTransferForm({...bankTransferForm, bank_name: e.target.value})}
                          placeholder="e.g., HDFC Bank, SBI"
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Remarks (Optional)</Label>
                        <Input
                          value={bankTransferForm.remarks}
                          onChange={e => setBankTransferForm({...bankTransferForm, remarks: e.target.value})}
                          placeholder="Any additional notes"
                          className="rounded-sm mt-1"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-6">
                      <Button onClick={submitBankTransfer} className="bg-[#1B2A4A] text-white rounded-sm">
                        Submit Transfer Details
                      </Button>
                      <Button variant="outline" onClick={() => setShowBankTransferModal(null)} className="rounded-sm">Cancel</Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Payment Modal with Razorpay */}
            {showPaymentModal && selectedNomination && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <Card className="max-w-md w-full mx-4">
                  <CardHeader className="bg-[#1B2A4A] text-white rounded-t-lg">
                    <CardTitle className="text-base">Pay Examination Fee</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="p-4 bg-slate-50 rounded-sm">
                        <p className="font-semibold text-[#1B2A4A]">{selectedNomination.full_name}</p>
                        <p className="text-sm text-slate-500">{selectedNomination.email}</p>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t">
                          <span className="text-sm text-slate-600">{selectedNomination.examination} Examination</span>
                          <span className="text-xl font-bold text-[#1B2A4A]">₹{selectedNomination.base_fee?.toLocaleString()}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 pt-4">
                        <Button variant="outline" onClick={() => { setShowPaymentModal(false); setSelectedNomination(null); }} className="flex-1 rounded-sm">
                          Cancel
                        </Button>
                        <PaymentButton
                          amount={selectedNomination.base_fee}
                          description={`${selectedNomination.examination} Exam Fee - ${selectedNomination.full_name}`}
                          onSuccess={(paymentId) => handleNominationPaymentSuccess(paymentId, selectedNomination)}
                          onError={(error) => toast.error(error || 'Payment failed')}
                          className="flex-1 bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-sm"
                        >
                          Pay ₹{selectedNomination.base_fee?.toLocaleString()}
                        </PaymentButton>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Registration Fee Bank Transfer Modal */}
            {showRegFeeTransferModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowRegFeeTransferModal(false)}>
                <Card className="max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                  <CardHeader className="bg-[#1B2A4A] text-white rounded-t-lg">
                    <CardTitle className="text-base">Submit Bank Transfer Details</CardTitle>
                    <CardDescription className="text-slate-300">Registration Fee Payment</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="p-3 bg-amber-50 rounded-sm border border-amber-200">
                        <p className="text-sm font-medium text-amber-800">Examination Center Registration Fee</p>
                        <p className="text-lg font-bold text-amber-700">₹{REGISTRATION_FEE.toLocaleString()}</p>
                      </div>
                      
                      <div className="p-3 bg-slate-50 rounded-sm">
                        <p className="text-xs font-semibold text-slate-600 mb-2">Bank Account Details</p>
                        <p className="text-sm"><span className="text-slate-500">Bank:</span> <span className="font-medium">Yes Bank</span></p>
                        <p className="text-sm"><span className="text-slate-500">Branch:</span> <span className="font-medium">Khar West</span></p>
                        <p className="text-sm"><span className="text-slate-500">Account:</span> <span className="font-medium">021188700000261</span></p>
                        <p className="text-sm"><span className="text-slate-500">IFSC:</span> <span className="font-medium">YESB0000211</span></p>
                        <p className="text-sm"><span className="text-slate-500">Name:</span> <span className="font-medium">International Medical Competency Accreditation Council</span></p>
                      </div>
                      
                      <div>
                        <Label className="text-sm font-medium">Transfer Reference / UTR Number *</Label>
                        <Input
                          value={bankTransferForm.transfer_reference}
                          onChange={e => setBankTransferForm({...bankTransferForm, transfer_reference: e.target.value})}
                          placeholder="Enter UTR/Reference number"
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Transfer Date *</Label>
                        <Input
                          type="date"
                          value={bankTransferForm.transfer_date}
                          onChange={e => setBankTransferForm({...bankTransferForm, transfer_date: e.target.value})}
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Your Bank Name *</Label>
                        <Input
                          value={bankTransferForm.bank_name}
                          onChange={e => setBankTransferForm({...bankTransferForm, bank_name: e.target.value})}
                          placeholder="Bank from which you transferred"
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Remarks (Optional)</Label>
                        <Textarea
                          value={bankTransferForm.remarks}
                          onChange={e => setBankTransferForm({...bankTransferForm, remarks: e.target.value})}
                          placeholder="Any additional notes"
                          className="rounded-sm mt-1"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-6">
                      <Button onClick={submitRegFeeBankTransfer} className="bg-[#1B2A4A] text-white rounded-sm">
                        Submit Transfer Details
                      </Button>
                      <Button variant="outline" onClick={() => setShowRegFeeTransferModal(false)} className="rounded-sm">Cancel</Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Financials Tab */}
            <TabsContent value="financials" className="p-5" data-testid="financials-tab-content">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#1B2A4A]">Financial Dashboard</h3>
                  <p className="text-sm text-slate-500">Track your 30% revenue share from student enrollments</p>
                </div>
                <Select value={financialPeriod} onValueChange={setFinancialPeriod}>
                  <SelectTrigger className="w-40 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="month">Last 30 Days</SelectItem>
                    <SelectItem value="quarter">Last 90 Days</SelectItem>
                    <SelectItem value="year">This Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {financialSummary && (
                <>
                  {/* Financial Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                      <CardContent className="p-4">
                        <IndianRupee className="h-8 w-8 mb-2 opacity-80" />
                        <p className="text-2xl font-bold">₹{financialSummary.summary.center_revenue?.toLocaleString()}</p>
                        <p className="text-xs opacity-80">Your Revenue (30%)</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                      <CardContent className="p-4">
                        <CreditCard className="h-8 w-8 mb-2 opacity-80" />
                        <p className="text-2xl font-bold">₹{financialSummary.summary.total_revenue?.toLocaleString()}</p>
                        <p className="text-xs opacity-80">Total Revenue</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                      <CardContent className="p-4">
                        <Clock className="h-8 w-8 mb-2 opacity-80" />
                        <p className="text-2xl font-bold">₹{financialSummary.summary.pending_center_share?.toLocaleString()}</p>
                        <p className="text-xs opacity-80">Pending Earnings</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                      <CardContent className="p-4">
                        <TrendingUp className="h-8 w-8 mb-2 opacity-80" />
                        <p className="text-2xl font-bold">₹{financialSummary.summary.total_discounts_given?.toLocaleString()}</p>
                        <p className="text-xs opacity-80">Discounts Given</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Monthly Breakdown Chart */}
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle className="text-base">Monthly Revenue Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {financialSummary.monthly_breakdown?.length > 0 ? (
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={financialSummary.monthly_breakdown}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, '']} />
                              <Bar dataKey="center_share" fill="#C5A572" name="Your Share (30%)" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <p className="text-center py-8 text-slate-500">No revenue data yet</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Revenue Share - Only Center Share */}
                  <Card className="border-l-4 border-l-[#C5A572]">
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-[#1B2A4A] mb-2">Your Revenue Share</h4>
                      <div className="text-center">
                        <p className="text-slate-500 text-sm">Examination Center Share</p>
                        <p className="text-4xl font-bold text-[#C5A572]">30%</p>
                        <p className="text-xs text-slate-400 mt-1">of each examination fee collected</p>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* Results & Statistics Tab */}
            <TabsContent value="results" className="p-5" data-testid="results-tab-content">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1B2A4A]">Student Results & Statistics</h3>
                  <p className="text-sm text-slate-500">View examination results for enrolled students</p>
                </div>
                <div className="flex gap-2">
                  <Select value={resultFilter || "all"} onValueChange={(v) => setResultFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-32 rounded-sm"><SelectValue placeholder="All Results" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Results</SelectItem>
                      <SelectItem value="pass">Passed</SelectItem>
                      <SelectItem value="fail">Failed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={examFilter || "all"} onValueChange={(v) => setExamFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-32 rounded-sm"><SelectValue placeholder="All Exams" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Exams</SelectItem>
                      <SelectItem value="CBEAM">CBEAM</SelectItem>
                      <SelectItem value="CBELM">CBELM</SelectItem>
                      <SelectItem value="CBERM">CBERM</SelectItem>
                      <SelectItem value="CBEAD">CBEAD</SelectItem>
                      <SelectItem value="CBENA">CBENA</SelectItem>
                      <SelectItem value="CBEAA">CBEAA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Statistics Cards */}
              {statistics && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <Card className="bg-slate-50">
                    <CardContent className="p-4 text-center">
                      <Users className="h-6 w-6 mx-auto mb-1 text-slate-600" />
                      <p className="text-2xl font-bold">{statistics.total_students}</p>
                      <p className="text-xs text-slate-500">Total Students</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50">
                    <CardContent className="p-4 text-center">
                      <CheckCircle className="h-6 w-6 mx-auto mb-1 text-green-600" />
                      <p className="text-2xl font-bold text-green-600">{statistics.results.pass}</p>
                      <p className="text-xs text-slate-500">Passed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50">
                    <CardContent className="p-4 text-center">
                      <XCircle className="h-6 w-6 mx-auto mb-1 text-red-600" />
                      <p className="text-2xl font-bold text-red-600">{statistics.results.fail}</p>
                      <p className="text-xs text-slate-500">Failed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50">
                    <CardContent className="p-4 text-center">
                      <Clock className="h-6 w-6 mx-auto mb-1 text-amber-600" />
                      <p className="text-2xl font-bold text-amber-600">{statistics.results.pending}</p>
                      <p className="text-xs text-slate-500">Pending</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50">
                    <CardContent className="p-4 text-center">
                      <TrendingUp className="h-6 w-6 mx-auto mb-1 text-blue-600" />
                      <p className="text-2xl font-bold text-blue-600">{statistics.pass_rate}%</p>
                      <p className="text-xs text-slate-500">Pass Rate</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Student Results Table */}
              {studentResults.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <GraduationCap className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No results match your filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F8FAFC]">
                        <TableHead className="text-xs font-bold">Student</TableHead>
                        <TableHead className="text-xs font-bold">Examination</TableHead>
                        <TableHead className="text-xs font-bold">Score</TableHead>
                        <TableHead className="text-xs font-bold">MCQ</TableHead>
                        <TableHead className="text-xs font-bold">OSCE</TableHead>
                        <TableHead className="text-xs font-bold">Case Log</TableHead>
                        <TableHead className="text-xs font-bold">Viva</TableHead>
                        <TableHead className="text-xs font-bold">Result</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {studentResults.map((r) => (
                        <TableRow key={r.enrollment_id}>
                          <TableCell>
                            <p className="text-sm font-medium text-[#1B2A4A]">{r.student_name}</p>
                            <p className="text-xs text-slate-500">{r.student_email}</p>
                          </TableCell>
                          <TableCell><Badge className="bg-slate-100">{r.examination_code}</Badge></TableCell>
                          <TableCell className="font-semibold">
                            {r.score?.total_weighted_score ? `${r.score.total_weighted_score.toFixed(1)}%` : '-'}
                          </TableCell>
                          <TableCell className="text-sm">{r.score?.mcq_percentage ? `${r.score.mcq_percentage.toFixed(0)}%` : '-'}</TableCell>
                          <TableCell className="text-sm">{r.score?.osce_score || '-'}</TableCell>
                          <TableCell className="text-sm">{r.score?.case_log_average?.toFixed(1) || '-'}</TableCell>
                          <TableCell className="text-sm">{r.score?.viva_overall || '-'}</TableCell>
                          <TableCell>
                            <Badge className={
                              r.result_status === 'pass' ? 'bg-green-100 text-green-700' :
                              r.result_status === 'fail' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }>
                              {r.score?.award_classification || r.result_status?.charAt(0).toUpperCase() + r.result_status?.slice(1)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Scheduled Exams Tab */}
            <TabsContent value="exams" className="p-5">
              <h2 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">
                Scheduled Examinations
                {allocatedCandidates.length > 0 && (
                  <Badge className="ml-2 bg-green-100 text-green-700">{allocatedCandidates.length} scheduled</Badge>
                )}
              </h2>
              
              {/* Allocated Candidates from Admin */}
              {allocatedCandidates.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-green-50">
                        <TableHead className="text-xs font-bold">Candidate</TableHead>
                        <TableHead className="text-xs font-bold">Contact</TableHead>
                        <TableHead className="text-xs font-bold">Examination</TableHead>
                        <TableHead className="text-xs font-bold">Date & Time</TableHead>
                        <TableHead className="text-xs font-bold">Examiner</TableHead>
                        <TableHead className="text-xs font-bold">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allocatedCandidates.map((alloc) => (
                        <TableRow key={alloc.allocation_id}>
                          <TableCell>
                            <p className="text-sm font-medium text-[#1B2A4A]">{alloc.candidate?.name}</p>
                            <p className="text-xs text-slate-400">{alloc.candidate?.registration_number}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-xs text-slate-600">{alloc.candidate?.email}</p>
                            <p className="text-xs text-slate-500">{alloc.candidate?.phone}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className="text-xs rounded-sm bg-purple-100 text-purple-700">{alloc.examination?.type}</Badge>
                            <p className="text-xs text-slate-500 mt-1">{alloc.examination?.name}</p>
                            <p className="text-xs text-slate-400">Route {alloc.examination?.route}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-medium text-[#1B2A4A]">{alloc.schedule?.date}</p>
                            <p className="text-xs text-slate-500">{alloc.schedule?.time || 'Time TBD'}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm text-slate-600">{alloc.examiner?.name}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-xs rounded-sm ${
                              alloc.schedule?.status === 'scheduled' ? 'bg-green-100 text-green-700' :
                              alloc.schedule?.status === 'rescheduled' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {alloc.schedule?.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : dashboardData?.upcoming_exams?.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F8FAFC]">
                        <TableHead className="text-xs font-bold">Candidate</TableHead>
                        <TableHead className="text-xs font-bold">Exam Type</TableHead>
                        <TableHead className="text-xs font-bold">Date</TableHead>
                        <TableHead className="text-xs font-bold">Time</TableHead>
                        <TableHead className="text-xs font-bold">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.upcoming_exams?.map((exam) => (
                        <TableRow key={exam.id}>
                          <TableCell>
                            <p className="text-sm font-medium text-[#1B2A4A]">{exam.candidate_name}</p>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-xs rounded-sm">OSCE</Badge></TableCell>
                          <TableCell className="text-sm">{exam.exam_date}</TableCell>
                          <TableCell className="text-sm">{exam.exam_time}</TableCell>
                          <TableCell>
                            <Badge className="bg-blue-100 text-blue-700 text-xs rounded-sm">{exam.status || 'Scheduled'}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No examinations scheduled yet.</p>
              )}
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#1B2A4A]">Messages</h3>
                <Button onClick={() => setShowMessageForm(!showMessageForm)} className="bg-[#C5A572] text-white rounded-sm text-sm">
                  <Send className="h-4 w-4 mr-1" /> New Message
                </Button>
              </div>

              {showMessageForm && (
                <Card className="mb-4 border-2 border-[#C5A572]">
                  <CardContent className="p-4">
                    <form onSubmit={sendMessage} className="space-y-3">
                      <div>
                        <Label className="text-xs">Subject</Label>
                        <Input
                          value={messageForm.subject}
                          onChange={e => setMessageForm({...messageForm, subject: e.target.value})}
                          placeholder="Message subject"
                          className="mt-1 rounded-sm"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Message</Label>
                        <Textarea
                          value={messageForm.message}
                          onChange={e => setMessageForm({...messageForm, message: e.target.value})}
                          placeholder="Write your message to IMCAC..."
                          className="mt-1 rounded-sm"
                          rows={4}
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="bg-[#1B2A4A] text-white rounded-sm text-sm" disabled={sending}>
                          {sending ? 'Sending...' : 'Send Message'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowMessageForm(false)} className="rounded-sm text-sm">Cancel</Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">No Messages Yet</h3>
                  <p className="text-sm text-slate-500">Send a message to IMCAC for any queries</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`border rounded-sm p-4 ${msg.direction === 'outgoing' ? 'border-[#C5A572] bg-[#C5A572]/5' : 'border-slate-200'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge className={`text-xs mb-1 ${msg.direction === 'outgoing' ? 'bg-[#C5A572] text-white' : 'bg-blue-100 text-blue-700'}`}>
                            {msg.direction === 'outgoing' ? 'From IMCAC' : 'Sent'}
                          </Badge>
                          <h4 className="text-sm font-semibold text-[#1B2A4A]">{msg.subject}</h4>
                          <p className="text-sm text-slate-600 mt-1">{msg.message}</p>
                        </div>
                        <span className="text-xs text-slate-400">{new Date(msg.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}
